// src/pages/admin/AdminOrders.jsx
import React, { useEffect, useState } from "react";
import { getAllOrders, updateOrderStatus, deleteOrder, ORDER_STATUSES } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import Modal from "../../components/common/Modal";
import { format } from "date-fns";
import { FiEye, FiSearch, FiTrash2, FiFileText, FiChevronLeft, FiChevronRight, FiPackage } from "react-icons/fi";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoImage  from "../../assests/logo.png";
const STATUS_COLORS = {
  Pending:   "bg-yellow-100 text-yellow-700 border-yellow-200",
  Confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  Shipped:   "bg-indigo-100 text-indigo-700 border-indigo-200",
  Delivered: "bg-green-100 text-green-700 border-green-200",
  Cancelled: "bg-red-100 text-red-700 border-red-200",
};

// ─── Company details ────────────────────────────────────────────────────────
const COMPANY = {
  name: "TRIPURA AGRO INDUSTRY(OPC) PVT LTD",
  address: "DIGHAPHANDI,DIST-GANJAM,ODISHA-761012",
  gstin: "GSTIN/UNI : 21AAJCT1379F1ZG",
  phone: "PH-9778553533",
  stateLine: "State Name : Odisha, Code : 21",
  bankName: "IDFC First Bank Gold A/c No.59778553533",
  bankAccNo: "59778553533",
  bankIfsc: "Branch & IFS Code : Berhampur & IDFB0060743",
  // Base64 data URL of the logo image. Paste your logo's base64 string here,
  // e.g. "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...".
  // Leave as null to skip drawing a logo (a blank box placeholder is shown instead).
  logoDataUrl: logoImage ,
};

// ─── Number to words (for "Amount Chargeable in words") ───────────────────────
function numberToWords(num) {
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  function inWords(n) {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + inWords(n % 100) : "");
    if (n < 100000) return inWords(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + inWords(n % 1000) : "");
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + inWords(n % 100000) : "");
    return inWords(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + inWords(n % 10000000) : "");
  }

  const rupees = Math.floor(num);
  const words = rupees === 0 ? "Zero" : inWords(rupees);
  return `INR ${words} Only`;
}

// ─── Invoice Generator (Tax Invoice style) ─────────────────────────────────────
function generateInvoice(order, allOrders = []) {
  const doc = new jsPDF({ compress: true });

  // jsPDF's default fonts (Helvetica/Times) only support the WinAnsi/Latin-1
  // character set. Anything outside that range — Devanagari (Hindi) text,
  // emoji, smart quotes, the ₹ symbol, etc. — renders as garbled characters
  // instead of an error, e.g. "Cumin (जीरा)" becoming "Cumin (@0>)". Strip
  // those characters out before they reach the PDF so invoices stay clean.
  // Once the non-ASCII text is gone, a name like "Peas Flour (मटर)" would
  // otherwise leave a dangling empty "()" behind, so clean that up too.
  const toPdfSafeText = (str) => {
    if (str == null) return "";
    return String(str)
      .replace(/[^\x20-\x7E]/g, "")
      .replace(/\(\s*\)/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const pageWidth = doc.internal.pageSize.getWidth();
  const left = 14;
  const right = pageWidth - 14;

  // Sequential invoice number: position of this order among all orders,
  // sorted oldest → newest (1, 2, 3, ...). Falls back to the order ID if
  // the order can't be found in the list (e.g. invoice opened standalone).
  const sortedOrders = [...allOrders].sort((a, b) => {
    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
    return aTime - bTime;
  });
  const orderIndex = sortedOrders.findIndex((o) => o.id === order.id);
  const invoiceNo = orderIndex >= 0 ? String(orderIndex + 1) : order.id.slice(0, 8).toUpperCase();
  const orderDate = order.createdAt?.toDate
    ? format(order.createdAt.toDate(), "dd-MMM-yy")
    : "—";

  let y = 14;

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(17, 24, 39);
  doc.text("INVOICE", pageWidth / 2, y, { align: "center" });
  y += 6;

  const boxTop = y;
  doc.setDrawColor(0, 0, 0);

  // ── Logo (top-left, before company name) ──
  const logoSize = 18;
  const logoX = left + 2;
  const textX = left + 2 + logoSize + 3; // company text starts after logo

  if (COMPANY.logoDataUrl) {
    try {
      doc.addImage(COMPANY.logoDataUrl, "PNG", logoX, y + 2, logoSize, logoSize);
    } catch (e) {
      // If the logo fails to load, fall back to a placeholder box
      doc.rect(logoX, y + 2, logoSize, logoSize);
    }
  } else {
    // Placeholder box where the logo will go — replace COMPANY.logoDataUrl with
    // a base64 data URL to render your actual logo here.
    doc.setDrawColor(180, 180, 180);
    doc.rect(logoX, y + 2, logoSize, logoSize);
    doc.setFontSize(6);
    doc.setTextColor(160, 160, 160);
    doc.text("LOGO", logoX + logoSize / 2, y + 2 + logoSize / 2 + 1, { align: "center" });
    doc.setDrawColor(0, 0, 0);
    doc.setTextColor(0, 0, 0);
  }

  // ── Seller (top-left, next to logo) ──
  const sellerMaxWidth = (pageWidth / 2 + 2) - textX - 2; // stop before the vertical divider
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  const nameLines = doc.splitTextToSize(COMPANY.name, sellerMaxWidth);
  doc.text(nameLines, textX, y + 5);
  const nameBlockHeight = nameLines.length * 4.2;

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  let sellerY = y + 5 + nameBlockHeight + 2.5;
  doc.text(doc.splitTextToSize(COMPANY.address, sellerMaxWidth), textX, sellerY);
  sellerY += 4.5;
  doc.text(COMPANY.gstin, textX, sellerY);
  sellerY += 4.5;
  doc.text(COMPANY.phone, textX, sellerY);
  sellerY += 4.5;
  doc.text(COMPANY.stateLine, textX, sellerY);

  // ── Invoice No. & Date (top-right) ──
  const metaX = pageWidth / 2 + 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Invoice No.", metaX, y + 6);
  doc.text("Dated", metaX + 55, y + 6);
  doc.setFont("helvetica", "bold");
  doc.text(invoiceNo, metaX, y + 12);
  doc.text(orderDate, metaX + 55, y + 12);

  const headerRowHeight = Math.max(31, sellerY - y + 6);
  y += headerRowHeight;
  doc.line(left, y, right, y);

  // ── Buyer (Bill to) ──
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Buyer (Bill to)", left + 2, y + 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text(toPdfSafeText(order.userName) || "—", left + 2, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);

  const addr = order.shippingAddress;
  let buyerLineY = y + 18;
  if (addr) {
    const addrStr = toPdfSafeText(`${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`);
    const lines = doc.splitTextToSize(addrStr, (pageWidth - 28) * 0.6);
    doc.text(lines, left + 2, buyerLineY);
    buyerLineY += lines.length * 5;
  }
  doc.text(`Phone: ${order.userPhone || "—"}`, left + 2, buyerLineY);
  buyerLineY += 5;
  if (order.userEmail) {
    doc.text(`Email: ${order.userEmail}`, left + 2, buyerLineY);
    buyerLineY += 5;
  }

  // ── Right column: Delivery Note / Mode of Payment / Bill of Lading / Vehicle No ──
  const payX = pageWidth / 2 + 6;
  const colDividerX = pageWidth / 2 + 2 + (right - (pageWidth / 2 + 2)) / 2;
  const pay2X = colDividerX + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);

  // Row A: Delivery Note | Mode/Terms of Payment
  doc.text("Delivery Note", payX, y + 6);
  doc.text("Mode/Terms of Payment", pay2X, y + 6);
  doc.setFont("helvetica", "bold");
  const paymentModeLabel =
    order.paymentMethod === "COD" || !order.paymentMethod
      ? "Cash On Delivery (COD)"
      : "Paid Online";
  doc.text(paymentModeLabel, pay2X, y + 12);

  // Row B: Bill of Lading/LR-RR No. | Motor Vehicle No.
  const rowDividerY = y + 16;
  doc.setFont("helvetica", "normal");
  doc.text("Bill of Lading/LR-RR No.", payX, rowDividerY + 6);
  doc.text("Motor Vehicle No.", pay2X, rowDividerY + 6);
  if (order.vehicleNo) {
    doc.setFont("helvetica", "bold");
    doc.text(order.vehicleNo, pay2X, rowDividerY + 12);
  }

  const rightColBottom = rowDividerY + 16; // bottom edge of the right column's content

  y = Math.max(buyerLineY, rightColBottom) + 4;
  doc.line(left, y, right, y);
  y += 4;

  // Outer box around the whole header
  doc.rect(left, boxTop, right - left, y - boxTop);
  doc.line(pageWidth / 2 + 2, boxTop, pageWidth / 2 + 2, y);
  doc.line(left, boxTop + headerRowHeight, pageWidth / 2 + 2, boxTop + headerRowHeight);

  // Sub-divider inside the right column (splits each row into two label/value pairs)
  doc.line(colDividerX, boxTop + headerRowHeight, colDividerX, y);
  // Horizontal divider between Row A and Row B within the right column
  doc.line(pageWidth / 2 + 2, rowDividerY, right, rowDividerY);

  y += 2;

  // ── Items table ──
  const items = order.items || [];

  // Resolve per-item display fields, preferring rich product data
  // (weight/pack size, unit, per-unit rate, HSN/SAC) when present on the
  // order item, and falling back to sensible derived values when it isn't.
  const resolvedItems = items.map((item) => {
    const qty = Number(item.qty) || 0;
    const unit = item.unit || "Pcs";          // "kg", "pack", or fallback
    const isPack = unit === "pack";
    const hsn = item.hsnSac || "";

    // Pack size (units per pack) or weight (kg) per line, whichever applies
    const packOrWeight = isPack
      ? (item.packSize != null ? Number(item.packSize) : null)
      : (item.weightSize != null ? Number(item.weightSize) : null);

    // Quantity column: show total weight/pack count when known (qty × size),
    // otherwise just the qty.
    const totalQty = packOrWeight ? packOrWeight * qty : qty;
    const unitLabel = isPack ? "pack" : (packOrWeight ? "kg" : unit);
    const quantityLabel = packOrWeight
      ? `${totalQty.toLocaleString("en-IN")} ${unitLabel}`
      : `${qty}`;

    // Rate column: prefer the explicit per-unit price stored on the order
    // item; otherwise derive it from the line total ÷ total quantity.
    const explicitRate = isPack ? item.pricePerPack : item.pricePerKg;
    const rate = explicitRate != null
      ? Number(explicitRate)
      : (totalQty ? Number(item.price) * qty / totalQty : Number(item.price));

    const amount = Number(item.price) * qty;

    return { name: toPdfSafeText(item.name), hsn: toPdfSafeText(hsn), quantityLabel, rate, unit: unitLabel, amount, totalQty };
  });

  // ── Delivery charge / SGST / CGST — stored on the order at checkout ──
  const subtotal        = order.subtotal != null ? Number(order.subtotal) : resolvedItems.reduce((s, it) => s + it.amount, 0);
  const invTotalKg       = Number(order.totalKg) || 0;
  const invDeliveryCharge = Number(order.deliveryCharge) || 0;
  const invSgstPercent   = order.sgstPercent != null ? Number(order.sgstPercent) : 0;
  const invCgstPercent   = order.cgstPercent != null ? Number(order.cgstPercent) : 0;
  const invSgstAmount    = Number(order.sgstAmount) || 0;
  const invCgstAmount    = Number(order.cgstAmount) || 0;
  const grandTotal       = Number(order.total) || 0;

  autoTable(doc, {
    startY: y,
    head: [["Sl\nNo.", "Description of Goods", "HSN/SAC", "Quantity", "Rate", "per", "Amount"]],
    body: resolvedItems.map((it, i) => [
      i + 1,
      it.name,
      it.hsn,
      it.quantityLabel,
      it.rate.toFixed(2),
      it.unit,
      it.amount.toFixed(2),
    ]),
    foot: [
      [{ content: "Subtotal", colSpan: 6, styles: { halign: "right", fontStyle: "normal" } },
        { content: subtotal.toFixed(2), styles: { halign: "right", fontStyle: "normal" } }],
      [{ content: `Delivery Charge${invTotalKg > 0 ? ` (${invTotalKg.toFixed(2)} kg)` : ""}`, colSpan: 6, styles: { halign: "right", fontStyle: "normal" } },
        { content: invDeliveryCharge.toFixed(2), styles: { halign: "right", fontStyle: "normal" } }],
      [{ content: `SGST (${invSgstPercent}%)`, colSpan: 6, styles: { halign: "right", fontStyle: "normal" } },
        { content: invSgstAmount.toFixed(2), styles: { halign: "right", fontStyle: "normal" } }],
      [{ content: `CGST (${invCgstPercent}%)`, colSpan: 6, styles: { halign: "right", fontStyle: "normal" } },
        { content: invCgstAmount.toFixed(2), styles: { halign: "right", fontStyle: "normal" } }],
      [{ content: "Grand Total", colSpan: 6, styles: { halign: "right", fontStyle: "bold" } },
        { content: `Rs.${grandTotal.toFixed(2)}`, styles: { halign: "right", fontStyle: "bold" } }],
    ],
    theme: "grid",
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8.5, fontStyle: "bold", lineColor: [0, 0, 0], lineWidth: 0.2 },
    footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8.5, fontStyle: "bold", lineColor: [0, 0, 0], lineWidth: 0.2 },
    bodyStyles: { fontSize: 8.5, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2 },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: "auto" },
      2: { cellWidth: 20, halign: "center" },
      3: { cellWidth: 24, halign: "right" },
      4: { cellWidth: 18, halign: "right" },
      5: { cellWidth: 14, halign: "center" },
      6: { cellWidth: 26, halign: "right" },
    },
    margin: { left, right: 14 },
    minCellHeight: 8,
  });

  let finalY = doc.lastAutoTable.finalY;

  // ── Amount in words + bank details block ──
  const blockTop = finalY;
  doc.rect(left, blockTop, right - left, 34);
  doc.line(pageWidth / 2 + 2, blockTop, pageWidth / 2 + 2, blockTop + 34);

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.text("Amount Chargeable (in words)", left + 2, blockTop + 6);
  doc.setFont("helvetica", "bold");
  const wordsLines = doc.splitTextToSize(numberToWords(Number(order.total)), (pageWidth - 28) * 0.45);
  doc.text(wordsLines, left + 2, blockTop + 12);
  doc.setFont("helvetica", "normal");
  doc.text("E. & O.E", right - 2, blockTop + 6, { align: "right" });

  const bankX = pageWidth / 2 + 6;
  doc.text("Company's Bank Details", bankX, blockTop + 6);
  doc.text(`Bank Name : ${COMPANY.bankName}`, bankX, blockTop + 12);
  doc.text(`A/c No.   : ${COMPANY.bankAccNo}`, bankX, blockTop + 18);
  doc.text(COMPANY.bankIfsc, bankX, blockTop + 24);

  finalY = blockTop + 34;

  // ── Declaration + Signatory ──
  const declTop = finalY;
  doc.rect(left, declTop, right - left, 22);
  doc.line(pageWidth / 2 + 2, declTop, pageWidth / 2 + 2, declTop + 22);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Declaration", left + 2, declTop + 6);
  const declText = "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.";
  const declLines = doc.splitTextToSize(declText, (pageWidth - 28) * 0.45);
  doc.text(declLines, left + 2, declTop + 11);

  doc.setFont("helvetica", "bold");
  const forCompanyLines = doc.splitTextToSize(`for ${COMPANY.name}`, right - (pageWidth / 2 + 6) - 2);
  doc.text(forCompanyLines, right - 2, declTop + 6, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.text("Authorised Signatory", right - 2, declTop + 19, { align: "right" });

  finalY = declTop + 22 + 8;

  // Footer note
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text("This is a Computer Generated Invoice", pageWidth / 2, finalY, { align: "center" });

  const orderIdShort = order.id.slice(0, 8).toUpperCase();
  doc.save(`Invoice_${orderIdShort}.pdf`);
}

// "30 kg" / "5 pack(s)" depending on the order item's unit
function getQtyLabel(item) {
  if (item.unit === "pack") return item.packSize ? `${item.packSize} pack(s)` : null;
  return item.weightSize ? `${item.weightSize} kg` : null;
}

// "₹107/kg" / "₹99/pack" depending on the order item's unit
function getRateLabel(item) {
  if (item.unit === "pack") return item.pricePerPack ? `₹${item.pricePerPack}/pack` : null;
  return item.pricePerKg ? `₹${item.pricePerKg}/kg` : null;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function AdminOrders() {
  const [orders,       setOrders]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState(null);
  const [filter,       setFilter]       = useState("All");
  const [search,       setSearch]       = useState("");
  const [updating,     setUpdating]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // order to confirm delete
  const [currentPage,  setCurrentPage]  = useState(1);
  const [invoiceGeneratingId, setInvoiceGeneratingId] = useState(null); // order id currently generating a PDF
  const PAGE_SIZE = 10;

  useEffect(() => {
    getAllOrders().then((o) => { setOrders(o); setLoading(false); });
  }, []);

  // Reset to page 1 whenever the filter or search changes the result set
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search]);

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      await updateOrderStatus(orderId, newStatus);
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o));
      if (selected?.id === orderId) setSelected((s) => ({ ...s, status: newStatus }));
      toast.success(`Status updated to ${newStatus}`);
    } catch { toast.error("Update failed"); }
    finally { setUpdating(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteOrder(deleteTarget.id);
      setOrders((prev) => prev.filter((o) => o.id !== deleteTarget.id));
      if (selected?.id === deleteTarget.id) setSelected(null);
      toast.success("Order deleted");
    } catch { toast.error("Delete failed"); }
    finally { setDeleteTarget(null); }
  };

  // Generating the PDF (drawing text/table + encoding the image) runs
  // synchronously and blocks the main thread, so the click can feel
  // unresponsive with no visual feedback until the download pops up.
  // Set the "generating" state first, then defer the actual heavy work
  // one tick later so React gets a chance to paint the spinner first.
  const handleDownloadInvoice = (order) => {
    if (invoiceGeneratingId) return; // avoid double-clicks stacking up work
    setInvoiceGeneratingId(order.id);
    setTimeout(() => {
      try {
        generateInvoice(order, orders);
      } catch (e) {
        toast.error("Failed to generate invoice");
      } finally {
        setInvoiceGeneratingId(null);
      }
    }, 30);
  };

  const filtered = orders.filter((o) => {
    const matchStatus = filter === "All" || o.status === filter;
    const matchSearch = !search || o.id.includes(search)
      || (o.userName || "").toLowerCase().includes(search.toLowerCase())
      || (o.userPhone || "").includes(search);
    return matchStatus && matchSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  // Compact page-number list with ellipses, e.g. 1 … 4 5 [6] 7 8 … 12
  const getPageNumbers = () => {
    const pages = [];
    const windowSize = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= safePage - windowSize && i <= safePage + windowSize)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "…") {
        pages.push("…");
      }
    }
    return pages;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage and track all customer orders</p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-sm font-semibold border border-primary-100">
          <FiPackage className="w-4 h-4" /> {orders.length} total
        </span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, ID, phone..."
            className="input pl-9 py-2 text-sm w-64" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["All", ...ORDER_STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filter === s ? "bg-primary-600 text-white border-primary-600 shadow-sm" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden border border-gray-200 rounded-xl shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["Order ID", "Customer", "Items", "Total", "Payment", "Date", "Status", "Action"].map((h) => (
                    <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-16">
                      <div className="flex flex-col items-center justify-center text-gray-400 gap-2">
                        <FiPackage className="w-8 h-8" />
                        <p className="text-sm font-medium text-gray-500">No orders found</p>
                        <p className="text-xs text-gray-400">Try adjusting your search or filter</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((order, idx) => (
                    <tr key={order.id} className={`transition-colors hover:bg-primary-50/40 ${idx % 2 === 1 ? "bg-gray-50/50" : "bg-white"}`}>
                      <td className="px-4 py-3.5 font-mono text-xs font-medium text-gray-600">#{order.id.slice(0, 8).toUpperCase()}</td>
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-gray-900">{order.userName}</p>
                        <p className="text-xs text-gray-500">{order.userPhone}</p>
                      </td>
                      <td className="px-4 py-3.5 text-gray-600">{order.items?.length} item{order.items?.length !== 1 ? "s" : ""}</td>
                      <td className="px-4 py-3.5 font-semibold text-primary-600">₹{Number(order.total || 0).toFixed(2)}</td>
                      <td className="px-4 py-3.5">
                        {order.paymentMethod === "COD" || !order.paymentMethod ? (
                          <span className="inline-block text-xs font-medium px-2 py-1 rounded-md bg-gray-100 text-gray-700 border border-gray-200">COD</span>
                        ) : (
                          <span className="inline-block text-xs font-medium px-2 py-1 rounded-md bg-green-100 text-green-700 border border-green-200">
                            Paid Online
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">
                        {order.createdAt?.toDate ? format(order.createdAt.toDate(), "dd MMM, hh:mm a") : "—"}
                      </td>
                      <td className="px-4 py-3.5">
                        {order.status === "Cancelled" ? (
                          <span className={`inline-block text-xs font-medium px-2 py-1.5 rounded-lg border ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-700"}`}>
                            Cancelled
                          </span>
                        ) : (
                          <select
                            value={order.status}
                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                            disabled={updating}
                            className={`text-xs font-medium px-2 py-1.5 rounded-lg border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-200 ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-700"}`}
                          >
                            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                      </td>

                      {/* ── Action column: View + Invoice + Delete ── */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1">
                          {/* View */}
                          <button
                            onClick={() => setSelected(order)}
                            title="View order"
                            className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors">
                            <FiEye className="w-4 h-4" />
                          </button>

                          {/* Invoice */}
                          <button
                            onClick={() => handleDownloadInvoice(order)}
                            disabled={invoiceGeneratingId === order.id}
                            title="Download invoice"
                            className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 transition-colors disabled:opacity-60">
                            {invoiceGeneratingId === order.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <FiFileText className="w-4 h-4" />
                            )}
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => setDeleteTarget(order)}
                            title="Delete order"
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination footer ── */}
          {filtered.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50/50">
              <p className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-700">{startIdx + 1}</span>–
                <span className="font-medium text-gray-700">{Math.min(startIdx + PAGE_SIZE, filtered.length)}</span> of{" "}
                <span className="font-medium text-gray-700">{filtered.length}</span> orders
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <FiChevronLeft className="w-4 h-4" />
                </button>

                {getPageNumbers().map((p, i) =>
                  p === "…" ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-xs text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`min-w-[2rem] h-8 px-2 rounded-lg text-xs font-medium border transition-colors ${p === safePage ? "bg-primary-600 text-white border-primary-600 shadow-sm" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                      {p}
                    </button>
                  )
                )}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Order detail modal ── */}
      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title={`Order #${selected?.id?.slice(0, 8).toUpperCase()}`} size="lg">
        {selected && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500">Customer:</span> <span className="font-medium ml-1">{selected.userName}</span></div>
              <div><span className="text-gray-500">Phone:</span> <span className="font-medium ml-1">{selected.userPhone}</span></div>
              <div><span className="text-gray-500">Email:</span> <span className="font-medium ml-1">{selected.userEmail}</span></div>
              <div>
                <span className="text-gray-500">Payment:</span>{" "}
                {selected.paymentMethod === "COD" || !selected.paymentMethod ? (
                  <span className="font-medium ml-1">Cash on Delivery</span>
                ) : (
                  <span className="font-medium ml-1 text-green-700">
                    Paid Online via Razorpay
                    {selected.razorpayPaymentId ? ` (ID: ${selected.razorpayPaymentId})` : ""}
                  </span>
                )}
              </div>
              <div className="col-span-2">
                <span className="text-gray-500">Address:</span>
                <span className="font-medium ml-1">
                  {selected.shippingAddress?.address}, {selected.shippingAddress?.city}, {selected.shippingAddress?.state} – {selected.shippingAddress?.pincode}
                </span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Items</h4>
              <div className="space-y-2">
                {selected.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center">🌱</div>}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.qty} × ₹{item.price}</p>
                      {(getQtyLabel(item) || getRateLabel(item)) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {getQtyLabel(item) && (
                            <span className="inline-block text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-1.5 py-0.5">
                              📦 {getQtyLabel(item)}
                            </span>
                          )}
                          {getRateLabel(item) && (
                            <span className="inline-block text-xs text-primary-600 bg-primary-50 border border-primary-100 rounded-md px-1.5 py-0.5">
                              {getRateLabel(item)}
                            </span>
                          )}
                          {item.hsnSac && (
                            <span className="inline-block text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-1.5 py-0.5">
                              HSN/SAC: {item.hsnSac}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="font-semibold">₹{item.price * item.qty}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal</span>
                  <span className="font-medium text-gray-700">
                    ₹{(selected.subtotal != null ? Number(selected.subtotal) : selected.items?.reduce((s, i) => s + Number(i.price) * Number(i.qty), 0) || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Delivery Charge{selected.totalKg > 0 ? ` (${Number(selected.totalKg).toFixed(2)} kg)` : ""}</span>
                  <span className="font-medium text-gray-700">₹{(Number(selected.deliveryCharge) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>SGST ({selected.sgstPercent ?? 0}%)</span>
                  <span className="font-medium text-gray-700">₹{(Number(selected.sgstAmount) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>CGST ({selected.cgstPercent ?? 0}%)</span>
                  <span className="font-medium text-gray-700">₹{(Number(selected.cgstAmount) || 0).toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between font-bold text-base">
                <span>Total</span><span className="text-primary-600">₹{Number(selected.total || 0).toFixed(2)}</span>
              </div>
            </div>

            {selected.status === "Cancelled" ? (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Status</h4>
                <span className={`inline-block px-3 py-1.5 rounded-lg text-sm font-medium border ${STATUS_COLORS["Cancelled"] || "bg-red-100 text-red-700 border-red-200"}`}>
                  Cancelled
                </span>
                <p className="text-xs text-gray-500 mt-1.5">This order was cancelled and can no longer be updated.</p>
              </div>
            ) : (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Update Status</h4>
                <div className="flex flex-wrap gap-2">
                  {ORDER_STATUSES.map((s) => (
                    <button key={s} onClick={() => handleStatusChange(selected.id, s)} disabled={updating}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${selected.status === s ? "bg-primary-600 text-white border-primary-600" : "bg-white text-gray-600 border-gray-300 hover:border-primary-400"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Invoice button inside modal too */}
            <div className="pt-1">
              <button
                onClick={() => handleDownloadInvoice(selected)}
                disabled={invoiceGeneratingId === selected.id}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-60">
                {invoiceGeneratingId === selected.id ? (
                  <><Spinner size="sm" /> Generating...</>
                ) : (
                  <><FiFileText className="w-4 h-4" /> Download Invoice</>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete confirmation modal ── */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Order" size="sm">
        {deleteTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete order{" "}
              <span className="font-mono font-semibold text-gray-900">#{deleteTarget.id.slice(0, 8).toUpperCase()}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors">
                Delete
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}